import { decryptData } from "./crypto";
import store from "./store";

export function hasPermission(permission) {
  // Prefer Vuex store (fast Set lookups), fallback to legacy encrypted localStorage.
  try {
    const set = store?.getters?.permissionsSet;
    if (set && typeof set.has === "function") {
      return set.has(permission);
    }
  } catch (e) {
    // ignore
  }

  const userPermissions = localStorage.getItem("user_pemissions");
  if (userPermissions) {
    const permissions = decryptData(userPermissions); // Decrypt the permissions
    return permissions?.includes(permission);
  }
  return false;
}

// New API naming in this repo's UI work uses "hasPerm" semantics.
export function hasPerm(name) {
  return hasPermission(name);
}

export function hasAnyPermission(names) {
  if (!Array.isArray(names) || names.length === 0) return false;
  return names.some((n) => hasPermission(n));
}

export function hasAnyPerm(names) {
  return hasAnyPermission(names);
}

export function hasAllPermissions(names) {
  if (!Array.isArray(names) || names.length === 0) return true;
  return names.every((n) => hasPermission(n));
}

// Stage-aware / granular checks (preferred naming for new permissions work)
// Be resilient when permissions are missing/null: return false (or true for empty arrays in canAll).
export function can(permission) {
  if (!permission) return false;
  return hasPermission(permission);
}

export function canAny(perms) {
  if (!Array.isArray(perms) || perms.length === 0) return false;
  return perms.some((p) => can(p));
}

export function canAll(perms) {
  if (!Array.isArray(perms) || perms.length === 0) return true;
  return perms.every((p) => can(p));
}

// Filters a nested menu config:
// - Removes any item that has `requiredPermission` and the user lacks it
// - Collapses/removes a parent if its `children` becomes empty after filtering
export function filterMenu(menu, hasPermissionFn = hasPerm) {
  const list = Array.isArray(menu) ? menu : [];
  const filtered = list
    .map((item) => {
      if (!item || typeof item !== "object") return null;

      const required = item.requiredPermission;
      if (required && !hasPermissionFn(required)) return null;

      const children = Array.isArray(item.children) ? item.children : null;
      if (children) {
        const nextChildren = filterMenu(children, hasPermissionFn);
        if (nextChildren.length === 0) return null;
        return { ...item, children: nextChildren };
      }

      return item;
    })
    .filter(Boolean);

  return filtered;
}
